import { PHONE_COUNTRY_CODE, PHONE_NATIONAL_DIGITS } from '../../core/constants';

/** Каноническая форма `+998XXXXXXXXX`: именно она уходит в API и лежит в `AuthUser.phone`. */

/** Национальная часть без кода страны: 9 цифр. */
const NATIONAL_LENGTH = PHONE_NATIONAL_DIGITS;

function digitsOf(raw: string): string {
  return raw.replace(/\D/g, '');
}

/**
 * `998` без плюса режется только при избытке цифр: иначе номер `99 8XX-XX-XX` потерял бы свой префикс.
 * Ведущая `8` — привычка набирать межгород; отрезается только при длине 10.
 */
function nationalOf(raw: string): string {
  const digits = digitsOf(raw);
  // По плюсу, а не по числу цифр: неполная маска `+998 90 123-4` — те же 9 цифр, что и целый номер.
  if (raw.trimStart().startsWith(PHONE_COUNTRY_CODE)) {
    return digits.slice(3);
  }
  if (digits.startsWith('998') && digits.length > NATIONAL_LENGTH) {
    return digits.slice(3);
  }
  if (digits.startsWith('8') && digits.length === NATIONAL_LENGTH + 1) {
    return digits.slice(1);
  }
  return digits;
}

/**
 * Для поля ввода: ведущий `+998` или `998 ` (плюс стёрли) — уже нарисованная маска, а не начало номера.
 * `998` в начале номера снимается только при избытке цифр: иначе номер `99 812-34-56` стал бы чужим.
 */
function nationalOfInput(raw: string): string {
  const digits = digitsOf(raw);
  let rest = /^\s*(\+998|998\D)/.test(raw) ? digits.slice(3) : digits;
  if (rest.startsWith('998') && rest.length > NATIONAL_LENGTH) {
    rest = rest.slice(3);
  }
  if (rest.startsWith('8') && rest.length === NATIONAL_LENGTH + 1) {
    return rest.slice(1);
  }
  return rest;
}

/** `+998 XX XXX-XX-XX` по цифрам национальной части (в т.ч. неполной). */
function mask(national: string): string {
  let out = `${PHONE_COUNTRY_CODE} ${national.slice(0, 2)}`;
  if (national.length > 2) {
    out += ` ${national.slice(2, 5)}`;
  }
  if (national.length > 5) {
    out += `-${national.slice(5, 7)}`;
  }
  if (national.length > 7) {
    out += `-${national.slice(7, 9)}`;
  }
  return out;
}

/** Мусор без цифр даёт `+998`: валидность проверяется отдельно, `isValidUzPhone`. */
export function normalizePhone(raw: string): string {
  return PHONE_COUNTRY_CODE + nationalOf(raw).slice(0, NATIONAL_LENGTH);
}

/** Ровно PHONE_NATIONAL_DIGITS цифр после `+998` — ни больше, ни меньше. */
export function isValidUzPhone(raw: string): boolean {
  return nationalOf(raw).length === NATIONAL_LENGTH;
}

/** Для подписи «SMS-код отправлен: …». Чужой формат возвращается как есть. */
export function formatPhoneForDisplay(phone: string): string {
  const national = nationalOf(phone);
  return national.length === NATIONAL_LENGTH ? mask(national) : phone;
}

/** `tel:`-ссылка из номера в произвольном формате бэкенда; непригодный номер — null, кнопки нет. */
export function telHref(raw: string): string | null {
  // `+` пропускается только ведущий: строка с бэкенда недоверенная, внутри номера он сломал бы набор.
  const dialable = `${raw.trimStart().startsWith('+') ? '+' : ''}${digitsOf(raw)}`;
  return /^\+?\d{3,20}$/.test(dialable) ? `tel:${dialable}` : null;
}

/** Пустой ввод даёт пустую строку: иначе поле нельзя очистить и не покажется плейсхолдер. */
export function formatPhoneInput(raw: string): string {
  const national = nationalOfInput(raw).slice(0, NATIONAL_LENGTH);
  return national === '' ? '' : mask(national);
}
